import { Component, Injector } from '@angular/core';
import { ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { AbstractPrintModal, IGenericPrintModalData } from '@blcloud/bl-ng-share-module';
import { Observable } from 'rxjs/Observable';
import { of as ObservableOf } from 'rxjs/observable/of';
import { forkJoin as ObservableForkJoin } from 'rxjs/observable/forkJoin';
import { IItemServiceSetting, IReserveSearchCondition, IGenericPrintInstruction, IGenericPrintCondition } from '@blcloud/bl-datamodel';
import { ReserveSearchReportType } from '@blcloud/bl-datamodel/enum/reserve/reserve-search-report-type';
import { ReserveSearchStatusArray } from '@blcloud/bl-datamodel/enum/reserve/reserve-search-status';
import { CarEnterDivArray } from '@blcloud/bl-datamodel/enum/deals/car-enter-div';
import { ReserveTallyUnitDivArray } from '@blcloud/bl-datamodel/enum/reserve/reserve-tally-unit-div';
import { DateTimeUtils } from '@blcloud/bl-common';
import { BlValidatorService, BlFormGroup, BlValidator, Rules } from '@blcloud/bl-ng-ui-component';
import { ReservePrintModalService } from './reserve-print-modal.service';
import { RESERVE_PRINT_MODAL_DEFINE } from './reserve-print-modal.define';
import * as moment from 'moment';
import { isNil as _isNil, forEach as _forEach } from 'lodash';

/**
 * 予約印刷モーダル起動パラメータ
 */
export interface IReservePrintModalData extends IGenericPrintModalData {
  /** 予約検索帳票タイプ */
  reserveSearchReportType: string;

    /** 予約検索条件 */
  reserveSearchCondition?: IReserveSearchCondition;
}

/**
 * 予約印刷モーダルウィンドウ
 */
@Component({
  templateUrl: 'reserve-print-modal.component.html',
  styleUrls: ['reserve-print-modal.component.scss'],
  providers: [ReservePrintModalService]
})
export class ReservePrintModalComponent extends AbstractPrintModal {

  /** 起動パラメータ */
  data: IReservePrintModalData;

  /** 予約検索条件 */
  reserveSearchCondition: IReserveSearchCondition;

  /** 商品サービス設定リスト */
  itemServicesettingList: IItemServiceSetting[];

  /** フォームグループ */
  blFormGroup: BlFormGroup;

  /** 予約検索ステータス */
  readonly ReserveSearchStatusArray = ReserveSearchStatusArray;

  /** 入庫区分 */
  readonly CarEnterDivArray = CarEnterDivArray;

  /** 予約集計単位区分 */
  readonly ReserveTallyUnitDivArray = ReserveTallyUnitDivArray;

  /**
   * コンストラクタ
   * @param injector Injector
   * @param service 汎用印刷サービス
   * @param validatorService 入力検証サービス
   */
  constructor(
    injector: Injector,
    private service: ReservePrintModalService,
    private validatorService: BlValidatorService
  ) {
    super(injector, service);
  }

  /**
   * @override
   */
  onAfterShown() {
    // 予約検索条件を取得
    this.service.getReserveSearchCondition(this.data).subscribe(
      reserveSearchCondition => {
        this.reserveSearchCondition = reserveSearchCondition;
      }
    );

    // 予約台数実績集計の場合
    if (this.isNumberCarsSummary) {
      ObservableForkJoin(
        // 商品サービス設定を取得
        this.service.searchItemServicesettingList(),
        // 入力検証ルールを取得を取得
        this.service.getReserveSearchConditionRules()
      ).subscribe(
        ([itemServicesettingList, rules]) => {
          this.itemServicesettingList = itemServicesettingList;
          this.blFormGroup = this.createBlFormGroup(rules);
          this.focusPrintButton();
        }
      );
    } else {
      this.focusPrintButton();
    }
  }

  /**
   * @override
   */
  protected validate(_reportOutputDiv: string, _isPreview: boolean): Observable<boolean> {
    return ObservableOf(!_isNil(this.blFormGroup) ? this.blFormGroup.validate() : true);
  }

  /**
   * @override
   */
  protected updateGenericPrintInstruction(genericPrintInstruction: IGenericPrintInstruction) {
    // 汎用印刷条件配列を独自生成
    const genericPrintConditionList: IGenericPrintCondition[] = [
      {
        itemnID: 'data',
        extractConditionString: JSON.stringify(this.reserveSearchCondition) // 予約検索条件をJSONで設定
      }
    ];
    genericPrintInstruction.genericPrintConditionList = genericPrintConditionList;

    // 帳票用途コードは「0」固定
    genericPrintInstruction.reportUseCode = 0;

    return genericPrintInstruction;
  }

  onChangeSearchStartDate(value: string) {
    this.reserveSearchCondition.searchStartDate = value;

    // 相関チェックの相手側も検証
    this.blFormGroup.getValidator(RESERVE_PRINT_MODAL_DEFINE.USE_RULE_KEY.END_DATE).formControl.updateValueAndValidity();
  }

  onChangeSearchEndDate(value: string) {
    this.reserveSearchCondition.searchEndDate = value;

    // 相関チェックの相手側も検証
    this.blFormGroup.getValidator(RESERVE_PRINT_MODAL_DEFINE.USE_RULE_KEY.START_DATE).formControl.updateValueAndValidity();
  }

  /**
   * 予約台数実績集計か判定します。
   * @returns 判定結果
   */
  get isNumberCarsSummary(): boolean {
    if (_isNil(this.data)) {
      return false;
    }

    return this.data.reserveSearchReportType === ReserveSearchReportType.NumberCarsSummary;
  }

  /**
   * フォームグループを生成します。
   * @param rules 入力検証ルール
   * @returns フォームグループ
   */
  private createBlFormGroup(rules: Rules[]): BlFormGroup {
    // フォームグループ生成
    const blFormGroup = this.validatorService.getBlFormGroup(rules);

    // 開始日と終了日の相関チェックを追加
    _forEach(blFormGroup.validatorGroup, (validator: BlValidator, key: string) => {
      validator.appendFn(
        [this.dateRangeValidatorFn(this.reserveSearchCondition)],
        { dateRange: RESERVE_PRINT_MODAL_DEFINE.DATE_RANGE_ERROR_MESSAGE[key]  }
      );
    });

    return blFormGroup;
  }

  /**
   * 検索開始日と検索終了日の範囲が適切であるか検証します。
   * @param entity 予約検索条件
   * @returns ValidatorFn
   */
  private dateRangeValidatorFn(entity: IReserveSearchCondition): ValidatorFn {

    return (_control: AbstractControl): ValidationErrors | null => {

      // 開始/終了が共に存在する場合のみチェック
      if (_isNil(entity.searchStartDate) || _isNil(entity.searchEndDate)) {
        return null;
      }

      const start = moment(entity.searchStartDate, DateTimeUtils.formatString.date);
      const end = moment(entity.searchEndDate, DateTimeUtils.formatString.date);

      // 開始 < 終了であるかチェック
      const valid = start.isSameOrBefore(end);

      return valid ? null : { dateRange: true };
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/reserve-print-modal/reserve-print-modal.component.ts